import * as R from 'ramda';
// features
import PC from '../features/permission/role-permission';
// constants
import * as GC from '../constants';
// helpers
import * as H from './helpers';
import { mathRoundNumber } from './calc';
import { getWindowLocale } from './locale';
import { checkStringsContains } from './string';
import { getConfigValueFromStore } from './common';
import { showToastrMessageSimple } from './toastr';
//////////////////////////////////////////////////

// window
const getItemFromWindow = (itemName) => window[itemName]; // eslint-disable-line
const setWindowItemToNull = (itemName) => window[itemName] = null; // eslint-disable-line
const setItemToWindow = (itemName, itemData) => window[itemName] = itemData; // eslint-disable-line

// branch
const getBranchFromWindowByGuid = (guid) => R.prop(guid, getItemFromWindow('amousBranches'));

const getBranchNameFromWindowByGuid = (guid) =>
  R.path([guid, GC.BRANCH_NAME], getItemFromWindow('amousBranches'));

// amousContainerTypeList
const getAmousContainerTypeListFromWindow = () => R.pathOr([], ['amousContainerTypeList'], window);

// amousConfig
const getAmousConfigFromWindow = () => R.propOr(null, 'amousConfig', window);

const setAmousConfigToWindow = ({ configs = {}, dropdowns = {} } = {}) => {
  const amousConfigs = R.pathOr({}, ['amousConfig', 'configs'], window);
  const amousDropdowns = R.pathOr({}, ['amousConfig', 'dropdowns'], window);

  const amousConfig = {
    configs: R.mergeRight(amousConfigs, configs),
    dropdowns: R.mergeRight(dropdowns, amousDropdowns),
  };

  return setItemToWindow('amousConfig', amousConfig);
};

const getAmousConfigByNameFromWindow = (name) => getConfigValueFromStore(
  name,
  getAmousConfigFromWindow(),
);

const getAmousDropdownConfigOptionsByNameFromWindow = (name) => R.pathOr(
  [],
  ['dropdowns', name, 'options'],
  getAmousConfigFromWindow(),
);

// HACK: to using without import G.
window.getAmousConfigByNameFromWindow = getAmousConfigByNameFromWindow;

const getHiddenMenuItemsConfigFromWindow = () => getConfigValueFromStore(
  GC.UI_HIDDEN_MENU_ITEMS,
  getAmousConfigFromWindow(),
);

const getHiddenTabsConfigFromWindow = () => getConfigValueFromStore(
  GC.UI_HIDDEN_TABS,
  getAmousConfigFromWindow(),
);

const getUseCommissionAssignmentConfigFromWindow = () => getConfigValueFromStore(
  GC.GENERAL_BRANCH_USE_COMMISSION_ASSIGNMENT,
  getAmousConfigFromWindow(),
);

const getShowChassisSectionConfigFromWindow = () => getConfigValueFromStore(
  GC.TEL_DRIVER_RATE_SHOW_RAILWAY_SECTION,
  getAmousConfigFromWindow(),
);

const getConfigDriverRateShowStartFinishPointFromWindow = () => getConfigValueFromStore(
  GC.TEL_DRIVER_RATE_SHOW_START_FINISH_POINT,
  getAmousConfigFromWindow(),
);

const getConfigGeneralUomCalcDefaultUomSystemFromWindow = () => getConfigValueFromStore(
  GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM,
  getAmousConfigFromWindow(),
);

const getSaferWatchCustomerKeyConfigFromWindow = () => getConfigValueFromStore(
  GC.INTEGRATION_SAFER_WATCH_CUSTOMER_KEY,
  getAmousConfigFromWindow(),
);

const getAutodialAppConfigFromWindow = () => getConfigValueFromStore(
  GC.UI_LOADBOARD_AUTODIALAPPNAME,
  getAmousConfigFromWindow(),
);

const getCalendarTimeIntervalConfigFromWindow = () => getConfigValueFromStore(
  GC.UI_CALENDAR_TIME_INTERVAL,
  getAmousConfigFromWindow(),
);

const getConfigUiOrderOrderEntryFromWindow = () => getConfigValueFromStore(
  GC.UI_ORDER_ORDER_ENTRY,
  getAmousConfigFromWindow(),
);

const getConfigUiReplaceDollarSymbolFromWindow = () => getConfigValueFromStore(
  GC.UI_REPLACE_DOLLAR_SYMBOL_WITH_US,
  getAmousConfigFromWindow(),
);

const getConfigCarrierUseFleetTrailerFromWindow = () => getConfigValueFromStore(
  GC.CARRIER_GENERAL_USE_FLEET_TRAILER,
  getAmousConfigFromWindow(),
);

const showSaferWatch = () => H.isNotNilAndNotEmpty(getSaferWatchCustomerKeyConfigFromWindow());

const showAdvancePayment = () => R.pathOr(false, ['configs', 'showAdvancePayment'], getAmousConfigFromWindow());

const showTransportingCarrier = () =>
  R.path(['configs', 'showTransportingCarrier'], getAmousConfigFromWindow());

const getCarrierSynchronizationType = () =>
  R.path(['configs', 'carrierSynchronizationType'], getAmousConfigFromWindow());

const showCarrierSynchronization = () =>
  R.path(['configs', 'showCarrierSynchronization'], getAmousConfigFromWindow());

// amousCurrentUser
const getAmousCurrentUserFromWindow = () => R.propOr(null, 'amousCurrentUser', window);

const getAmousCurrentUserAuthoritiesFromWindow = () => R.pathOr([], ['amousCurrentUser', 'authorities'], window);

const hasAmousCurrentUserPermissionsCommon = (permissions) => {
  const authorities = getAmousCurrentUserAuthoritiesFromWindow();

  const permissionsToUse = H.ifElse(
    H.isString(permissions),
    R.of(Array, permissions),
    permissions,
  );

  return R.or(R.includes(PC.ANY, permissions), checkStringsContains(permissionsToUse, authorities));
};

const hasAmousCurrentUserPermissions = (permissions) => {
  if (H.isNilOrEmpty(permissions)) return false;

  return hasAmousCurrentUserPermissionsCommon(permissions);
};

const hasAmousCurrentUserPermissionsIfIsNotNil = (permissions) => {
  if (H.isNilOrEmpty(permissions)) return true;

  return hasAmousCurrentUserPermissionsCommon(permissions);
};

const hasNotAmousCurrentUserPermissions = (permissions) => R.not(
  hasAmousCurrentUserPermissions(permissions),
);

const getHasNotEditRatePermissionsByRateType = (isCarrierRate) => {
  const editRatePermissions = H.ifElse(
    isCarrierRate,
    [PC.CARRIER_RATE_WRITE],
    [PC.FLEET_RATE_WRITE],
  );

  return hasNotAmousCurrentUserPermissions(editRatePermissions);
};

const getAccessForAmousCurrentUserByPermissions = (has, notHas) => {
  let allow = true;

  if (R.and(H.isNotNil(has), H.isNotNil(notHas))) {
    allow = R.and(
      hasAmousCurrentUserPermissions(has),
      hasNotAmousCurrentUserPermissions(notHas),
    );
  } else if (H.isNotNil(has)) {
    allow = hasAmousCurrentUserPermissions(has);
  } else if (H.isNotNil(notHas)) {
    allow = hasNotAmousCurrentUserPermissions(notHas);
  }

  return allow;
};

const getAmousCurrentUserGuidFromWindow = () => R.path(
  ['user_guid'],
  getAmousCurrentUserFromWindow(),
);

const getAmousUserTypeFromWindow = () => R.propOr(null, 'user_type', getAmousCurrentUserFromWindow());

const getAmousCurrentUserBranchGuidFromWindow = () => R.propOr(
  null,
  'enterprise_guid',
  getAmousCurrentUserFromWindow(),
);

const getAmousCurrentUserLoginIdFromWindow = () => R.propOr(
  null,
  'login_id',
  getAmousCurrentUserFromWindow(),
);

const isCurrentUserSuperAdmin = ({ authorities }) => R.includes(
  PC.ROLE_LIUBANCHYK_SUPER_ADMIN,
  authorities,
);

const hasCurrentUserPermission = (permission, { authorities }) => R.includes(
  permission,
  authorities,
);

const isCurrentUserTypeCarrier = () => R.equals(
  getAmousUserTypeFromWindow(),
  GC.USER_ROLE_TYPE_CARRIER,
);

const isCurrentUserTypeCustomer = () => R.equals(
  getAmousUserTypeFromWindow(),
  GC.USER_ROLE_TYPE_CUSTOMER,
);

const getIsAmousUserSuperAdminFromWindow = () => isCurrentUserSuperAdmin(getAmousCurrentUserFromWindow());


const getHasAmousCurrentUserPermissionFromWindow = (permissionName) => hasCurrentUserPermission(
  permissionName,
  getAmousCurrentUserFromWindow(),
);

// amousCurrentBranch
const getAmousCurrentBranchFromWindow = () => R.propOr(null, 'amousCurrentBranch', window);

const getAmousCurrentBranchNormalizedCurrency = () => R.propOr(null, 'amousCurrentBranchNormalizedCurrency', window);

const getAmousCurrentBranchGuidFromWindow = () => R.propOr(
  null,
  GC.FIELD_GUID,
  getAmousCurrentBranchFromWindow(),
);

const getAmousCurrentBranchMasterGuidFromWindow = () => R.propOr(
  null,
  GC.FIELD_MASTER_GUID,
  getAmousCurrentBranchFromWindow(),
);

const isCurrentBranchTypeMaster = () => R.pathEq(
  GC.BRANCH_TYPE_ENUM_MASTER,
  [GC.BRANCH_TYPE],
  getAmousCurrentBranchFromWindow(),
);

const isCurrentBranchTypeCustomer = () => R.pathEq(
  GC.BRANCH_TYPE_ENUM_CUSTOMER,
  [GC.BRANCH_TYPE],
  getAmousCurrentBranchFromWindow(),
);

const isCurrentBranchTypeDivision = () => R.pathEq(
  GC.BRANCH_TYPE_ENUM_DIVISION,
  [GC.BRANCH_TYPE],
  getAmousCurrentBranchFromWindow(),
);

// amousBranchConfigs
const setAmousBranchConfigsToWindow = (branchGuid, data) => {
  if (R.isNil(window.amousBranchConfigs)) window.amousBranchConfigs = {};

  if (R.isNil(window.amousBranchConfigs[branchGuid])) window.amousBranchConfigs[branchGuid] = {};

  const { mappedConfigs, receivedConfigs } = data;
  const { configs, dropdowns } = mappedConfigs;

  const branchConfigs = R.pathOr({}, ['amousBranchConfigs', branchGuid, 'mappedConfigs', 'configs'], window);
  const branchDropdowns = R.pathOr({}, ['amousBranchConfigs', branchGuid, 'mappedConfigs', 'dropdowns'], window);
  const branchReceivedConfigs = R.pathOr([], ['amousBranchConfigs', branchGuid, 'receivedConfigs'], window);

  const dataToUse = {
    receivedConfigs: R.uniq(R.concat(receivedConfigs, branchReceivedConfigs)),

    mappedConfigs: {
      configs: R.mergeRight(branchConfigs, configs),
      dropdowns: R.mergeRight(branchDropdowns, dropdowns),
    },
  };

  window.amousBranchConfigs[branchGuid] = dataToUse;
};

const getAmousBranchConfigsFromWindow = (branchGuid) => R.path(
  ['amousBranchConfigs', branchGuid],
  window,
);

const getAmousBranchConfigFromWindow = (configName, branchGuid) => {
  const branchGuidToUse = R.or(branchGuid, getAmousCurrentBranchGuidFromWindow());
  const branchConfigs = getAmousBranchConfigsFromWindow(branchGuidToUse);
  const mappedConfigs = R.path(['mappedConfigs'], branchConfigs);

  return getConfigValueFromStore(configName, mappedConfigs);
};

const getAmousBranchReceivedConfigsFromWindow = (branchGuid) => {
  const branchGuidToUse = R.or(branchGuid, getAmousCurrentBranchGuidFromWindow());
  const branchConfigs = getAmousBranchConfigsFromWindow(branchGuidToUse);
  const receivedConfigs = R.pathOr([], ['receivedConfigs'], branchConfigs);

  return receivedConfigs;
};

const clearWindowAmousBranchConfigsByInterval = () => setInterval(() => {
  window.amousBranchConfigs = null;
}, 1800000);

// amousLoadDivisionMap
const initWindowAmousLoadDivisionMap = () => window.amousLoadDivisionMap = new Map();
const hasWindowAmousLoadDivision = (loadGuid) => window.amousLoadDivisionMap.has(loadGuid);
const getWindowAmousLoadDivision = (loadGuid) => window.amousLoadDivisionMap.get(loadGuid);
const deleteWindowAmousLoadDivision = (loadGuid) => window.amousLoadDivisionMap.delete(loadGuid);
const setWindowAmousLoadDivision = (
  loadGuid, divisionGuid,
) => window.amousLoadDivisionMap.set(loadGuid, divisionGuid);

const detectMobile = () => {
  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  return toMatch.some((toMatchItem) => (
    window.navigator.userAgent.match(toMatchItem)
  ));
};

const detectTablet = () => {
  const w = window.innerWidth;
  const ua = window.navigator.userAgent.toLowerCase();
  const isTabletUA = /ipad|tablet|(android(?!.*mobile))/i.test(ua);

  return isTabletUA || (w > 768 && w <= 1366);
};

const detectDevice = () => {
  const isMobile = detectMobile();
  const isTablet = detectTablet();

  setItemToWindow('isMobile', isMobile);
  setItemToWindow('isTablet', isTablet);
  setItemToWindow('isNotDesktop', R.or(isMobile, isTablet));
};

const isLandscape = () => window.matchMedia('(orientation: landscape)').matches;

const getCurrentZoomLevel = () => {
  const element = document.body;
  const actualWidth = element.offsetWidth;

  const rectWidth = element.getBoundingClientRect().width;

  return mathRoundNumber(window.devicePixelRatio * (actualWidth / rectWidth), 2);
};

const copyToClipboard = async (text) => {
  try {
    const clipboard = R.path(['navigator', 'clipboard'], window);

    await clipboard.writeText(text);

    showToastrMessageSimple('info', `${getWindowLocale('messages:copied', 'Copied:')} ${text}`);
  } catch (error) {
    showToastrMessageSimple('error', `${getWindowLocale('messages:failed-to-copy', 'Failed to copy:')} ${text}`);
  }
};

export {
  isLandscape,
  detectDevice,
  showSaferWatch,
  setItemToWindow,
  copyToClipboard,
  getItemFromWindow,
  showAdvancePayment,
  getCurrentZoomLevel,
  setWindowItemToNull,
  setAmousConfigToWindow,
  isCurrentUserSuperAdmin,
  showTransportingCarrier,
  isCurrentUserTypeCarrier,
  getAmousConfigFromWindow,
  hasCurrentUserPermission,
  isCurrentUserTypeCustomer,
  getBranchFromWindowByGuid,
  isCurrentBranchTypeMaster,
  showCarrierSynchronization,
  getAmousUserTypeFromWindow,
  isCurrentBranchTypeCustomer,
  isCurrentBranchTypeDivision,
  getCarrierSynchronizationType,
  getBranchNameFromWindowByGuid,
  getAmousCurrentUserFromWindow,
  setAmousBranchConfigsToWindow,
  getHiddenTabsConfigFromWindow,
  getAmousBranchConfigFromWindow,
  hasAmousCurrentUserPermissions,
  getAutodialAppConfigFromWindow,
  getAmousConfigByNameFromWindow,
  getAmousBranchConfigsFromWindow,
  getAmousCurrentBranchFromWindow,
  hasNotAmousCurrentUserPermissions,
  getAmousCurrentUserGuidFromWindow,
  getIsAmousUserSuperAdminFromWindow,
  getHiddenMenuItemsConfigFromWindow,
  getAmousCurrentBranchGuidFromWindow,
  getAmousContainerTypeListFromWindow,
  getAmousCurrentUserLoginIdFromWindow,
  getConfigUiOrderOrderEntryFromWindow,
  getShowChassisSectionConfigFromWindow,
  getHasNotEditRatePermissionsByRateType,
  getAmousCurrentBranchNormalizedCurrency,
  getAmousBranchReceivedConfigsFromWindow,
  clearWindowAmousBranchConfigsByInterval,
  getAmousCurrentUserBranchGuidFromWindow,
  getCalendarTimeIntervalConfigFromWindow,
  getAmousCurrentUserAuthoritiesFromWindow,
  getSaferWatchCustomerKeyConfigFromWindow,
  getConfigUiReplaceDollarSymbolFromWindow,
  hasAmousCurrentUserPermissionsIfIsNotNil,
  getAccessForAmousCurrentUserByPermissions,
  getAmousCurrentBranchMasterGuidFromWindow,
  getConfigCarrierUseFleetTrailerFromWindow,
  getHasAmousCurrentUserPermissionFromWindow,
  getUseCommissionAssignmentConfigFromWindow,
  getAmousDropdownConfigOptionsByNameFromWindow,
  getConfigDriverRateShowStartFinishPointFromWindow,
  getConfigGeneralUomCalcDefaultUomSystemFromWindow,
  // amousLoadDivisionMap
  setWindowAmousLoadDivision,
  getWindowAmousLoadDivision,
  hasWindowAmousLoadDivision,
  deleteWindowAmousLoadDivision,
  initWindowAmousLoadDivisionMap,
};
